import { useContext, useMemo, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { isEqual, isUndefined } from 'lodash-es';
import { useObservable, useObservableState } from 'observable-hooks';
import { distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import PropTypes from 'prop-types';
import { defaultPropTypes, FormContext, useIsOverridable, useIsOverridden } from '../utils';
import FormFieldContainer from './FormFieldContainer';
import Tooltip from '../../../utils/tooltip';
import Select from '../../form/Select';
import { emptyStringToNull, tryToBoolean } from '../../../utils/cast';
import { createFallbackName } from '../../../utils/string';
import { ANY } from '../../pages/measuringPoint/configHelper';

function useFilteredOptionsByConfigHelper(name, options) {
    const { configHelper } = useContext(FormContext);

    return useObservableState(
        useObservable(
            (inputs$) =>
                inputs$.pipe(
                    switchMap(([iName, iOptions, iConfigHelper]) => {
                        if (!iConfigHelper.isEnabledForField(iName)) {
                            // Return original options.
                            return of(iOptions);
                        }

                        return iConfigHelper.availableFields$.pipe(
                            // Stop emitting for fields not available.
                            filter((availableFields) =>
                                Object.keys(availableFields).includes(name)
                            ),
                            // Filter unavailable options.
                            map((availableFields) => {
                                // If the config helper specifies `__any__` as
                                // options for this field, we allow all the
                                // options and not filtering is needed.
                                if (availableFields[name] === ANY) {
                                    return iOptions;
                                }

                                return iOptions.filter(([key, _value]) =>
                                    availableFields[name].includes(key)
                                );
                            }),
                            // Avoid unnecessary emissions/rerenders.
                            distinctUntilChanged(isEqual)
                        );
                    })
                ),
            [name, options, configHelper]
        ),
        options
    );
}
function ChoiceField({ fieldOptions, extraChoiceOptions = [], addOptionWhenMissing = false }) {
    const { name, settings, required } = fieldOptions;

    const { control } = useFormContext();

    const PROJECT_OPTION = 'PROJECT_OPTION';

    const { parentValues, setOverriddenField } = useContext(FormContext);

    const parentValue = parentValues?.[name];
    const overridable = useIsOverridable(name);
    const overridden = useIsOverridden(name);

    const optionsArray = useMemo(() => Array.from(settings.options), [settings.options]);

    const originalOptions = useFilteredOptionsByConfigHelper(name, optionsArray);

    const { field } = useController({
        name,
        control,
        rules: { required },
        defaultValue: settings.default,
    });

    const addedOptionsWhenMissing = useRef(new Map());

    const options = useMemo(() => {
        const all = [...originalOptions, ...extraChoiceOptions];

        if (addOptionWhenMissing && !all.map(([key]) => key).includes(field.value)) {
            // Store the added missing key inside a ref. This ensures it remains
            // available even if you switch to another setting during the edit session.
            addedOptionsWhenMissing.current.set(field.value, createFallbackName(field.value));
        }

        addedOptionsWhenMissing.current.forEach((value, missingKey) => {
            // Here, we add the missing entries to the options. During the
            // lifetime of the page, `all` may be supplemented with extra options
            // from an API request, for example. In that case, we don't want to
            // add the previously stored missing key if it has been added to `all`
            // in the meantime.
            if (!all.some(([existingKey]) => existingKey === missingKey)) {
                all.push([missingKey, value]);
            }
        });

        if (overridable && !isUndefined(parentValue)) {
            const translation = all.find(([key]) => key === parentValue)?.[1];
            all.push([PROJECT_OPTION, `${translation} - ${gettext('project setting')}`]);
        }

        return all;
    }, [
        extraChoiceOptions,
        originalOptions,
        addOptionWhenMissing,
        field.value,
        overridable,
        parentValue,
    ]);

    function transformInput(value) {
        if (overridable && !overridden) {
            return PROJECT_OPTION;
        }

        return value ?? '';
    }

    function transformOutput(e) {
        const { value: rawValue } = e.target;
        const value = emptyStringToNull(tryToBoolean(rawValue));

        if (overridable) {
            if (value === PROJECT_OPTION && overridden) {
                setOverriddenField(name, false);
                return parentValue;
            }

            if (!overridden) {
                setOverriddenField(name, true, true);
            }
        }

        // Normal behavior.
        return value;
    }

    return (
        <FormFieldContainer fieldOptions={fieldOptions} showOverrideCheckbox={false} inline={true}>
            <Tooltip
                content={gettext('Note: changing this value will override the project settings.')}
                disabled={!overridable || overridden}
            >
                <Select
                    id={`id_${name}`}
                    placeholder={settings.placeholder}
                    onChange={(e) => field.onChange(transformOutput(e))}
                    value={transformInput(field.value)}
                    data={options}
                />
            </Tooltip>
        </FormFieldContainer>
    );
}
ChoiceField.propTypes = {
    ...defaultPropTypes,
    extraChoiceOptions: PropTypes.array,
    addOptionWhenMissing: PropTypes.bool,
};

export default ChoiceField;
